import React from "react";
import { COPY } from "@/data/waitlistOptions";
import { HERO_MEDIA, LOGO_SRC } from "@/config/media";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { LANDING } from "@/constants/testIds/waitlist";

// Cinematic hero. Background video plays only when a source exists AND the user
// has not requested reduced motion; otherwise the poster image is shown.
export const HeroSection = () => {
  const reducedMotion = usePrefersReducedMotion();
  const showVideo = !!HERO_MEDIA.videoSrc && !reducedMotion;

  return (
    <header
      data-testid={LANDING.hero}
      className="relative isolate overflow-hidden min-h-[72vh] sm:min-h-[78vh] flex flex-col"
    >
      {/* Background media (decorative — hidden from assistive tech) */}
      <div
        data-testid={LANDING.heroMedia}
        aria-hidden="true"
        className="absolute inset-0 -z-10"
      >
        {showVideo ? (
          <video
            data-testid={LANDING.heroVideo}
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster={HERO_MEDIA.poster}
            tabIndex={-1}
          >
            <source src={HERO_MEDIA.videoSrc} type="video/mp4" />
          </video>
        ) : (
          <img
            src={HERO_MEDIA.poster}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        )}
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" />
      </div>

      <div className="mx-auto w-full max-w-5xl px-6 sm:px-8 pt-10 sm:pt-16 pb-12 sm:pb-16">
        {/* Brand lockup near the top: descriptor spans the logo width */}
        <div className="w-full max-w-[187px] sm:max-w-[231px]">
          <img
            data-testid={LANDING.heroLogo}
            src={LOGO_SRC}
            alt={COPY.logoAlt}
            className="block w-full h-auto select-none"
            draggable="false"
          />
          <p className="mt-2.5 w-full text-center font-condensed font-medium uppercase text-white text-[0.6rem] sm:text-[0.68rem] tracking-[0.55em] sm:tracking-[0.66em] whitespace-nowrap">
            {COPY.brandKicker}
          </p>
        </div>

        {/* Headline + tagline sit lower for visual balance */}
        <div className="mt-[6vh] sm:mt-[8vh]">
          <h1
            data-testid={LANDING.heroHeadline}
            className="uppercase font-semibold leading-[1.05] text-white text-[2rem] sm:text-[2.625rem] lg:text-[3.25rem] tracking-[-0.01em]"
          >
            <span className="block">{COPY.headlineLine1}</span>
            <span className="block mt-[0.32em]">{COPY.headlineLine2}</span>
          </h1>

          <p
            data-testid={LANDING.heroTagline}
            className="mt-6 text-[#D9B872] text-base sm:text-lg max-w-xl"
          >
            {COPY.tagline}
          </p>
        </div>
      </div>

      {/* Thin gold divider into the waitlist section */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8A24C]/70 to-transparent" />
    </header>
  );
};
